import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { useNavigate } from "react-router-dom";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
    Loader2,
    UserPlus,
    MapPin,
    Users,
    Calendar,
    DollarSign,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatDate, toTitleCase } from "../utils/formatters";
import ServiceTypeFilter from "./ServiceTypeFilter";
export const OpenRequests = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [requests, setRequests] = useState([]);
    const [assigningId, setAssigningId] = useState(null);
    const [serviceTypeFilter, setServiceTypeFilter] = useState("");

    useEffect(() => {
        loadOpenRequests();
    }, []);

    const loadOpenRequests = async () => {
        try {
            setLoading(true);
            const response = await api.getOpenRequests();
            setRequests(response.data);
        } catch (error) {
            console.error("Failed to load open requests:", error);
            toast.error("Failed to load open requests");
        } finally {
            setLoading(false);
        }
    };

    console.log(serviceTypeFilter, "check");
    const filteredOpenRequests = requests.filter((req) => {
        if (!serviceTypeFilter) return true;

        return !!req[serviceTypeFilter];
    });

    const handleAssignToMe = async (requestId) => {
        try {
            setAssigningId(requestId);
            await api.assignRequestToMe(requestId);
            toast.success("Request assigned successfully!");
            // Remove the assigned request from the list
            setRequests(requests.filter((req) => req.id !== requestId));
            // Notify layout to refresh sidebar count
            window.dispatchEvent(new Event("requestAssigned"));
        } catch (error) {
            console.error("Failed to assign request:", error);
            const errorMsg =
                error.response?.data?.detail || "Failed to assign request";
            toast.error(errorMsg);
        } finally {
            setAssigningId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                        Open Requests
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Unassigned travel requests waiting for a salesperson
                    </p>
                </div>
                <Badge variant="outline" className="text-lg px-4 py-2">
                    {requests.length} Open Requests
                </Badge>
            </div>

            {requests.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <Sparkles className="w-16 h-16 text-gray-300 mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">
                            No Open Requests
                        </h3>
                        <p className="text-gray-500 text-center max-w-md">
                            All travel requests have been assigned. Check back
                            later for new opportunities!
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
                    {/* Filter Section */}
                    <div className="flex justify-center">
                        <ServiceTypeFilter
                            serviceTypeFilter={serviceTypeFilter}
                            setServiceTypeFilter={setServiceTypeFilter}
                            type="openRequests"
                        />
                    </div>

                    {/* Cards Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredOpenRequests.map((request) => (
                            <Card
                                key={request.id}
                                className="flex flex-col h-full hover:shadow-md hover:-translate-y-1 transition-all duration-200"
                            >
                                {/* Header */}
                                <CardHeader className="pb-2">
                                    <div className="flex justify-between items-start gap-3">
                                        <div className="min-w-0">
                                            <CardTitle className="text-base font-semibold truncate">
                                                {request.title}
                                            </CardTitle>
                                            <CardDescription className="text-xs text-gray-500 truncate">
                                                {request.destination ||
                                                    "No destination"}
                                            </CardDescription>
                                        </div>

                                        <Badge
                                            variant={
                                                request.status === "Created"
                                                    ? "default"
                                                    : "secondary"
                                            }
                                            className="text-xs shrink-0"
                                        >
                                            {request.status}
                                        </Badge>
                                    </div>
                                </CardHeader>

                                {/* Content */}
                                <CardContent className="flex flex-col flex-1 space-y-4">
                                    {/* Dates */}
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <p className="text-xs font-medium text-gray-700">
                                                Preferred Date
                                            </p>
                                            <p className="text-gray-500">
                                                {new Date(
                                                    request.start_date,
                                                ).toLocaleDateString("en-IN", {
                                                    day: "2-digit",
                                                    month: "short",
                                                })} - {request.end_date ? new Date(
                                                    request.end_date,
                                                ).toLocaleDateString("en-IN", {
                                                    day: "2-digit",
                                                    month: "short",
                                                }) : "N.A."}
                                            </p>
                                        </div>
                                    </div>

                                    {/* People + Budget */}
                                    <div className="flex justify-between text-sm">
                                        <div>
                                            <p className="text-xs font-medium text-gray-700">
                                                People
                                            </p>
                                            <p className="text-gray-500">
                                                {request.people_count}
                                            </p>
                                        </div>

                                        <div className="text-right">
                                            <p className="text-xs font-medium text-gray-700">
                                                Budget
                                            </p>
                                            <p className="text-gray-500 whitespace-nowrap">
                                                ₹
                                                {request.budget_min?.toLocaleString()}{" "}
                                                – ₹
                                                {request.budget_max?.toLocaleString()}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Services */}
                                    <div className="flex flex-wrap gap-1">
                                        {Object.entries(request)
                                            .filter(
                                                ([key, value]) =>
                                                    key.startsWith("is_") &&
                                                    value === true,
                                            )
                                            .map(([key]) => (
                                                <Badge
                                                    key={key}
                                                    variant="outline"
                                                    className="text-xs"
                                                >
                                                    {toTitleCase(key
                                                        .replace("is_", "")
                                                        .replace(
                                                            "_required",
                                                            "",
                                                        )
                                                        .replaceAll("_", " "))}
                                                </Badge>
                                            ))}
                                    </div>

                                    {/* Travel Type */}
                                    {request.type_of_travel?.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {request.type_of_travel.map(
                                                (type) => (
                                                    <Badge
                                                        key={type}
                                                        className="text-xs bg-orange-100 text-orange-700"
                                                    >
                                                        {toTitleCase(type)}
                                                    </Badge>
                                                ),
                                            )}
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="mt-auto space-y-2">
                                        {user?.role !== "customer" && (
                                            <Button
                                                onClick={() => navigate(`/requests/${request.id}`)}
                                                className="w-full border-slate-200 text-slate-700 hover:bg-slate-50"
                                                variant="outline"
                                                size="sm"
                                            >
                                                View Request
                                            </Button>
                                        )}

                                        {(user?.role === "admin" || user?.role === "sales" || user?.role === "operations") && (
                                            <Button
                                                onClick={() =>
                                                    handleAssignToMe(request.id)
                                                }
                                                disabled={
                                                    assigningId === request.id
                                                }
                                                className="w-full bg-orange-600 hover:bg-orange-700"
                                                size="sm"
                                            >
                                                {assigningId === request.id ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Assigning...
                                                    </>
                                                ) : (
                                                    <>
                                                        <UserPlus className="w-4 h-4 mr-2" />
                                                        Assign to Me
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
